import Vue from 'vue';
import RootState from '@/store/root_state';
import { ActionTree, Commit } from 'vuex';
import { EnrollmentState } from './state';
import { MicroserviceEnum, mutation, query } from '@/helpers/westeros';
import {
  NormalizedAdmission,
  NormalizedEnrollment,
  normalizeAdmission,
  normalizeEnrollment,
} from '@/store/graphql/normalizer';
import {
  getEnrollment,
  GetEnrollmentQueryResult,
} from '@/store/graphql/queries/westerosArcaneumQueries';
import {
  getAdmission,
  GetAdmissionQueryResult,
} from '@/store/graphql/queries/westerosAdmissionsQueries';
import {
  applyVoucher,
  ApplyVoucher,
  ApplyVoucherResult,
  releaseAddonModuleToUser,
  ReleaseAddonModuleToUserResult,
  subscribeInComplementaryModule,
  SubscribeInComplementaryModuleResult,
  upsertEnrollment,
  UpsertEnrollmentMutationInput,
  UpsertEnrollmentMutationResult,
} from '@/store/graphql/mutations/westerosArcaneumMutations';

import accounts from '@/helpers/accountsIframe';
import {
  UpsertAdmissionMutationInput,
  UpsertAdmissionMutationResult,
  upsertAdmission,
} from '@/store/graphql/mutations/westerosAdmissionsMutations';

export function setEnrollmentData(commit: Commit, enrollment: NormalizedEnrollment) {
  commit('SET_PARTNER_CODE', enrollment.partnerCode);
  commit('SET_ENROLLMENT_ID', enrollment.enrollmentId);
  commit('SET_PERSONAL_DATA', enrollment.personalData);
  commit('SET_SIGNUP_METHOD', enrollment.studentSignupMethod);
  commit('SET_ADDRESS_INFO', enrollment.studentAddress);
  commit('SET_DOCUMENTS_SENT', enrollment.areDocumentsSent);
  commit('SET_ENROLLMENT_STATUS', enrollment.enrollmentStatus);
  commit('SET_SPONSOR_ADDRESS_INFO', enrollment.sponsorAddress);
  commit('SET_CONTRACT_ACCEPTED', enrollment.isContractAccepted);
  commit('SET_ACADEMIC_FORMATION', enrollment.academicFormation);
  commit('SET_PERSONAL_DOCUMENTS', enrollment.personalDocuments);
  commit('SET_CURRENT_PAYMENT_METHOD', enrollment.currentPaymentMethod);
  commit('SET_CURRENT_PAYMENT_STATUS', enrollment.currentPaymentStatus);
  commit('SET_CURRENT_PAYMENT_RECURRENT_CREDIT_CARD', enrollment.currentPaymentRecurrentCreditCard);
  commit('SET_SPONSOR_DOCUMENTS', enrollment.financialSponsorDocuments);
  commit('SET_IDENTIFICATION_DOCUMENT', enrollment.identificationDocument);
  commit('SET_PERSONAL_DATA_FINANCIAL_SPONSOR', enrollment.personalDataFinancialSponsor);
  commit(
    'SET_IDENTIFICATION_DOCUMENT_FINANCIAL_SPONSOR',
    enrollment.identificationFinancialSponsor,
  );
  commit('SET_PERSONAL_ACADEMIC_RECORD', enrollment.backofficeStudentAcademicRecord);
}

export function setAdmissionData(commit: Commit, admission: NormalizedAdmission) {
  commit('SET_COURSE', admission.course);
  commit('SET_CAMPUS', admission.campus);
}

const actions: ActionTree<EnrollmentState, RootState> = {
  async GET_ENROLLMENT({ getters, commit, dispatch }): Promise<boolean | undefined> {
    const authToken = Vue.$cookies.get('admissions-auth-token');

    if (!authToken) throw new Error('Forbidden');

    try {
      if (!getters.course) {
        query<GetAdmissionQueryResult>(MicroserviceEnum.ADMISSIONS, getAdmission).then(
          ({ data }) => {
            const admission = normalizeAdmission(data.getAdmission);

            setAdmissionData(commit, admission);
          },
        );
      }

      dispatch('PULL_IS_JACAD_COURSE');

      const { data } = await query<GetEnrollmentQueryResult>(
        MicroserviceEnum.ARCANEUM,
        getEnrollment,
      );

      const enrollment = normalizeEnrollment(data.getEnrollment);

      setEnrollmentData(commit, enrollment);

      return true;
    } catch (err) {
      if ((err as Error).message === 'GraphQL error: Enrollment not found') return false;

      throw err;
    }
  },

  async UPSERT_ENROLLMENT({ commit }, payload: UpsertEnrollmentMutationInput): Promise<void> {
    const authToken = Vue.$cookies.get('admissions-auth-token');

    if (!authToken) throw new Error('Forbidden');

    const { data } = await mutation<UpsertEnrollmentMutationResult>(
      MicroserviceEnum.ARCANEUM,
      upsertEnrollment,
      { data: payload },
    );

    const enrollment = normalizeEnrollment(data?.upsertEnrollment);
    setEnrollmentData(commit, enrollment);
  },

  async UPSERT_ADMISSION({ commit }, payload: UpsertAdmissionMutationInput): Promise<void> {
    const authToken = Vue.$cookies.get('admissions-auth-token');

    if (!authToken) throw new Error('Forbidden');

    const { data } = await mutation<UpsertAdmissionMutationResult>(
      MicroserviceEnum.ADMISSIONS,
      upsertAdmission,
      { data: payload },
    );

    const admission = normalizeAdmission(data?.upsertAdmission);

    commit('SET_COURSE', admission.course);
    commit('SET_CAMPUS', admission.campus);
  },

  async APPLY_VOUCHER({ commit }, payload: ApplyVoucher): Promise<void> {
    const authToken = Vue.$cookies.get('admissions-auth-token');

    if (!authToken) throw new Error('Forbidden');
    try {
      const { data } = await mutation<ApplyVoucherResult>(
        MicroserviceEnum.ARCANEUM,
        applyVoucher,
        payload,
      );
      const isSuccess = data?.applyVoucher.success || false;
      commit('SET_HAS_ERROR_VOUCHER', !isSuccess);
    } catch (err) {
      commit('SET_HAS_ERROR_VOUCHER', true);
    }
  },

  async RELEASE_COMPLEMENTARY_MODULE_TO_USER({ commit, getters }): Promise<void> {
    const { course, backofficeStudentAcademicRecord, personalDataCpf } = getters;

    const payload = {
      descomplicaCourseId: course?.descomplicaProviderCode,
      descomplicaCurriculumId: course?.descomplicaCurriculumId,
      academicRecordId: backofficeStudentAcademicRecord,
      cpf: personalDataCpf,
    };

    try {
      const { data } = await mutation<
        ReleaseAddonModuleToUserResult | SubscribeInComplementaryModuleResult
      >(MicroserviceEnum.ARCANEUM, subscribeInComplementaryModule, payload);

      if (!data) throw new Error('Error releasing complementary module');

      const [result] = Object.values(data);

      commit('SET_IS_COMPLEMENTARY_MODULE_ENABLE', result?.success);
    } catch (err) {
      commit('SET_IS_COMPLEMENTARY_MODULE_ENABLE', false);
    }
  },
};

export default actions;
